/**
 * 범교과 주제 편성 시수(1·2학기 목표) — public.annual_cross_topic_plans
 * 테이블이 아직 없으면(마이그레이션 미적용) localStorage 로 대체 저장합니다.
 */
import { supabase } from '../supabaseClient';

export type AnnualCrossTopicPlanItem = {
  id: string;
  topic: string;
  term1Hours: number;
  term2Hours: number;
};

const TABLE = 'annual_cross_topic_plans';

function localKey(workspaceId: string, gradeId: string, academicYear: number): string {
  return `cp.annualCrossTopicPlans.v1:${workspaceId}:${gradeId}:${academicYear}`;
}

function toHours(v: unknown): number {
  const n = Math.floor(Number(v));
  return Number.isFinite(n) && n > 0 ? Math.min(n, 999) : 0;
}

export function normalizeCrossTopicPlanItems(raw: unknown): AnnualCrossTopicPlanItem[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const out: AnnualCrossTopicPlanItem[] = [];
  for (const r of raw) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    const topic = String(o.topic ?? '').trim().slice(0, 40);
    if (!topic || seen.has(topic)) continue;
    seen.add(topic);
    out.push({
      id: String(o.id ?? '').trim() || `t${Date.now().toString(36)}${out.length}`,
      topic,
      term1Hours: toHours(o.term1Hours),
      term2Hours: toHours(o.term2Hours),
    });
  }
  return out;
}

function isMissingTable(error: { code?: string; message?: string } | null): boolean {
  if (!error) return false;
  return (
    error.code === 'PGRST205' ||
    error.code === '42P01' ||
    /annual_cross_topic_plans/.test(error.message ?? '')
  );
}

function readLocal(key: string): AnnualCrossTopicPlanItem[] {
  try {
    return normalizeCrossTopicPlanItems(JSON.parse(window.localStorage.getItem(key) ?? '[]'));
  } catch {
    return [];
  }
}

export async function loadAnnualCrossTopicPlan(
  workspaceId: string,
  gradeId: string,
  academicYear: number
): Promise<{ items: AnnualCrossTopicPlanItem[]; storage: 'db' | 'local' }> {
  const key = localKey(workspaceId, gradeId, academicYear);
  const { data, error } = await supabase
    .from(TABLE)
    .select('topics')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId)
    .eq('academic_year', academicYear)
    .maybeSingle();
  if (error) {
    if (isMissingTable(error)) return { items: readLocal(key), storage: 'local' };
    throw new Error(error.message);
  }
  return { items: normalizeCrossTopicPlanItems((data as { topics?: unknown } | null)?.topics), storage: 'db' };
}

export async function saveAnnualCrossTopicPlan(
  workspaceId: string,
  gradeId: string,
  academicYear: number,
  items: AnnualCrossTopicPlanItem[]
): Promise<'db' | 'local'> {
  const topics = normalizeCrossTopicPlanItems(items);
  const key = localKey(workspaceId, gradeId, academicYear);
  const { error } = await supabase.from(TABLE).upsert(
    {
      workspace_id: workspaceId,
      grade_id: gradeId,
      academic_year: academicYear,
      topics,
      updated_at: new Date().toISOString(),
    },
    { onConflict: 'workspace_id,grade_id,academic_year' }
  );
  if (error) {
    if (isMissingTable(error)) {
      window.localStorage.setItem(key, JSON.stringify(topics));
      return 'local';
    }
    throw new Error(error.message);
  }
  return 'db';
}
